import { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import {
  AlertTriangle,
  CalendarIcon,
  Loader2,
  Lock,
  Plus,
  Trash2,
} from "lucide-react";
import { z } from "zod";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "~/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useAccountsByCompany } from "~/hooks/contabilidad/useAccounts";
import { useClosedMonthsSet } from "~/hooks/contabilidad/useClosedPeriods";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { useJournalsByCompany } from "~/hooks/contabilidad/useJournals";
import { cn } from "~/lib/utils";
import { useProfileStore } from "~/store/profileStore";
import type {
  AccountMovement,
  MoveLineInput,
  MovementFormData,
} from "~/types/contabilidad";

const formSchema = z.object({
  company_id: z.string().min(1, "Seleccione una empresa"),
  journal_id: z.string().min(1, "Seleccione un diario"),
  move_date: z.string().min(1, "Seleccione una fecha"),
  ref: z.string().min(2, "La referencia debe tener al menos 2 caracteres"),
});

interface MovimientoFormProps {
  initialData?: AccountMovement;
  onSubmit: (data: MovementFormData) => void;
  isLoading?: boolean;
  submitLabel?: string;
  // When true, the Proveedores (cuenta por pagar, código 2103·) lines are shown
  // locked/read-only and the rest (honorarios, gastos, anticipos, banco) stay fully
  // editable. Used for pago-a-proveedor drafts: the AP lines are owned by the
  // subledger and must not be rewritten (a DB guard blocks it), but Tesorería does
  // edit the additional lines directly on the comprobante. The submit only carries
  // the non-AP lines; the AP lines are preserved server-side.
  lockApLines?: boolean;
}

// AP control lines (Proveedores, código 2103·) are the ones locked on a
// supplier-payment draft — same rule as the DB guard.
const isApCode = (code?: string | null) =>
  (code ?? "").replace(/\D/g, "").startsWith("2103");

interface LineState {
  id: string; // temp id for tracking
  account_id: string;
  description: string;
  debit: number;
  credit: number;
  // AP (Proveedores) line of a pago nómina — rendered read-only, excluded from submit.
  locked: boolean;
  // Cached "code - name" for display when the line is locked.
  accountLabel?: string;
}

const createEmptyLine = (): LineState => ({
  id: crypto.randomUUID(),
  account_id: "",
  description: "",
  debit: 0,
  credit: 0,
  locked: false,
});

export function MovimientoForm({
  initialData,
  onSubmit,
  isLoading = false,
  submitLabel = "Guardar",
  lockApLines = false,
}: MovimientoFormProps) {
  const { data: companies = [], isLoading: isLoadingCompanies } =
    useCompanies();
  const [lines, setLines] = useState<LineState[]>(() => {
    // Initialize lines from initialData if editing
    if (initialData?.lines && initialData.lines.length > 0) {
      return initialData.lines.map((line) => ({
        id: line.id || crypto.randomUUID(),
        account_id: line.account_id,
        description: line.description || "",
        debit: line.debit,
        credit: line.credit,
        locked: lockApLines && isApCode(line.account?.code),
        accountLabel: line.account
          ? `${line.account.code} - ${line.account.name}`
          : undefined,
      }));
    }
    return [createEmptyLine()];
  });

  const form = useForm<MovementFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_id: initialData?.company_id ?? "",
      journal_id: initialData?.journal_id ?? "",
      move_date: initialData?.move_date ?? dayjs().format("YYYY-MM-DD"),
      ref: initialData?.ref ?? "",
    },
  });

  const selectedCompanyId = form.watch("company_id");
  const selectedDate = form.watch("move_date");
  const { data: accounts = [] } = useAccountsByCompany(selectedCompanyId);
  const { data: journals = [], isLoading: isLoadingJournals } =
    useJournalsByCompany(selectedCompanyId);

  // Check closed periods
  const closedMonthsSet = useClosedMonthsSet();
  const role = useProfileStore((state) => state.role);
  const isAdmin = role === "superadmin" || role === "admin";

  // Check if selected month is closed
  const selectedMonth = selectedDate
    ? dayjs(selectedDate).format("YYYY-MM")
    : null;
  const isSelectedMonthClosed =
    selectedMonth && closedMonthsSet.has(selectedMonth);
  const showClosedWarning = isSelectedMonthClosed && !isAdmin;

  // Options for the account selector — searchable by both code and name
  const accountOptions = useMemo<SearchableSelectOption[]>(
    () =>
      accounts
        .filter((a) => a.is_active)
        .map((account) => ({
          value: account.id,
          label: `${account.code} - ${account.name}`,
          searchTerms: `${account.code} ${account.name}`,
        })),
    [accounts]
  );

  // Reset lines when company changes (accounts are different)
  useEffect(() => {
    if (!initialData && selectedCompanyId) {
      setLines([createEmptyLine()]);
    }
  }, [selectedCompanyId, initialData]);

  // Calculate totals
  const totalDebit = lines.reduce(
    (sum, line) => sum + (Number(line.debit) || 0),
    0
  );
  const totalCredit = lines.reduce(
    (sum, line) => sum + (Number(line.credit) || 0),
    0
  );
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const handleAddLine = () => {
    setLines([...lines, createEmptyLine()]);
  };

  const handleRemoveLine = (lineId: string) => {
    if (lines.length > 1) {
      setLines(lines.filter((l) => l.id !== lineId));
    }
  };

  const handleLineChange = (
    lineId: string,
    field: keyof LineState,
    value: string | number
  ) => {
    setLines(
      lines.map((line) =>
        line.id === lineId ? { ...line, [field]: value } : line
      )
    );
  };

  const handleSubmit = (data: MovementFormData) => {
    // On a pago-a-proveedor draft only the non-AP lines are submitted; the locked
    // AP (Proveedores) lines are preserved server-side and must never be rewritten
    // (the DB guard blocks it). Everything else behaves like a normal comprobante.
    const validLines: MoveLineInput[] = lines
      .filter((line) => !line.locked)
      .filter((line) => line.account_id && (line.debit > 0 || line.credit > 0))
      .map((line) => ({
        account_id: line.account_id,
        description: line.description || null,
        debit: Number(line.debit) || 0,
        credit: Number(line.credit) || 0,
      }));

    onSubmit({
      ...data,
      lines: validLines,
    });
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("es-CL").format(value);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="company_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Empresa *</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                disabled={isLoadingCompanies || !!initialData}
              >
                <FormControl>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Seleccione una empresa" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="journal_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Diario *</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                disabled={
                  isLoadingJournals || !selectedCompanyId || !!initialData
                }
              >
                <FormControl>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Seleccione un diario" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {journals.map((journal) => (
                    <SelectItem key={journal.id} value={journal.id}>
                      {journal.code} - {journal.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="move_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Fecha *</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full rounded-xl pl-3 text-left font-normal",
                        !field.value && "text-muted-foreground"
                      )}
                    >
                      {field.value ? (
                        dayjs(field.value).format("DD/MM/YYYY")
                      ) : (
                        <span>Seleccione fecha</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value ? new Date(field.value) : undefined}
                    onSelect={(date) =>
                      field.onChange(
                        date ? dayjs(date).format("YYYY-MM-DD") : ""
                      )
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        {showClosedWarning && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              El periodo contable de {dayjs(selectedDate).format("MMMM YYYY")}{" "}
              está cerrado. Solo administradores pueden registrar comprobantes en
              este mes.
            </AlertDescription>
          </Alert>
        )}

        <FormField
          control={form.control}
          name="ref"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Referencia *</FormLabel>
              <FormControl>
                <Input
                  placeholder="Descripción o referencia del comprobante"
                  className="rounded-xl"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Lines Table */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <FormLabel>Líneas del Comprobante</FormLabel>
            {!isBalanced && totalDebit > 0 && totalCredit > 0 && (
              <span className="text-sm text-destructive">
                El comprobante no está balanceado
              </span>
            )}
          </div>
          {lockApLines && (
            <p className="text-xs text-muted-foreground">
              Las líneas de Proveedores están bloqueadas. Puedes editar y agregar
              las demás (honorarios, gastos, anticipos, banco).
            </p>
          )}
          <div className="rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Cuenta</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="w-[120px] text-right">Debe</TableHead>
                  <TableHead className="w-[120px] text-right">Haber</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line) =>
                  line.locked ? (
                    <TableRow key={line.id} className="bg-muted/40">
                      <TableCell className="p-2 align-middle">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span>{line.accountLabel ?? "Proveedores"}</span>
                        </div>
                      </TableCell>
                      <TableCell className="p-2 align-middle text-sm text-muted-foreground">
                        {line.description || "-"}
                      </TableCell>
                      <TableCell className="p-2 text-right align-middle font-mono text-sm">
                        {formatNumber(Number(line.debit) || 0)}
                      </TableCell>
                      <TableCell className="p-2 text-right align-middle font-mono text-sm">
                        {formatNumber(Number(line.credit) || 0)}
                      </TableCell>
                      <TableCell className="p-1" />
                    </TableRow>
                  ) : (
                    <TableRow key={line.id}>
                      <TableCell className="p-1">
                        <SearchableSelect
                          value={line.account_id}
                          onValueChange={(value) =>
                            handleLineChange(line.id, "account_id", value)
                          }
                          options={accountOptions}
                          disabled={!selectedCompanyId}
                          placeholder="Selecciona cuenta"
                          emptyMessage="No se encontraron cuentas"
                        />
                      </TableCell>
                      <TableCell className="p-1">
                        <Input
                          value={line.description}
                          onChange={(e) =>
                            handleLineChange(
                              line.id,
                              "description",
                              e.target.value
                            )
                          }
                          placeholder=""
                          className="h-9 rounded-lg border bg-transparent focus-visible:ring-1"
                        />
                      </TableCell>
                      <TableCell className="p-1">
                        <Input
                          type="number"
                          value={line.debit || ""}
                          onChange={(e) =>
                            handleLineChange(
                              line.id,
                              "debit",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          placeholder="0"
                          className="h-9 rounded-lg border bg-transparent text-right focus-visible:ring-1"
                          min={0}
                        />
                      </TableCell>
                      <TableCell className="p-1">
                        <Input
                          type="number"
                          value={line.credit || ""}
                          onChange={(e) =>
                            handleLineChange(
                              line.id,
                              "credit",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          placeholder="0"
                          className="h-9 rounded-lg border bg-transparent text-right focus-visible:ring-1"
                          min={0}
                        />
                      </TableCell>
                      <TableCell className="p-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleRemoveLine(line.id)}
                          disabled={lines.length === 1}
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={2} className="font-medium">
                    Total
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatNumber(totalDebit)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatNumber(totalCredit)}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
          <div className="flex justify-end pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddLine}
              className="h-8 rounded-lg"
              disabled={!selectedCompanyId}
            >
              <Plus className="mr-1 h-4 w-4" />
              Agregar línea
            </Button>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            className="rounded-full px-6"
            disabled={
              isLoading ||
              (!lockApLines &&
                !isBalanced &&
                totalDebit > 0 &&
                totalCredit > 0) ||
              showClosedWarning
            }
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
